import React from "react";
import Logo from "./Logo";
import { resolveMediaUrl } from "../lib/api";

/**
 * Wraps any image/video and stamps the gold D watermark over it,
 * plus a corner caption ("DHADDA MOTION PICTURES").
 *
 * Props:
 *  - src: image URL (string)
 *  - videoSrc: video URL (string) — when set, renders <video>
 *  - alt
 *  - position: bottom-right | top-right | bottom-left | top-left
 *  - opacity: 0..1
 *  - watermarkSize: px
 *  - className, style
 */
export const WatermarkedMedia = ({
  src,
  videoSrc,
  alt = "",
  position = "bottom-right",
  opacity = 0.85,
  watermarkSize = 56,
  className = "",
  style = {},
  children,
  showCaption = true,
  testId,
}) => {
  const posClass =
    {
      "bottom-right": "bottom-3 right-3 md:bottom-4 md:right-4",
      "top-right": "top-3 right-3 md:top-4 md:right-4",
      "bottom-left": "bottom-3 left-3 md:bottom-4 md:left-4",
      "top-left": "top-3 left-3 md:top-4 md:left-4",
    }[position] || "bottom-3 right-3";

  const resolvedImg = resolveMediaUrl(src);
  const resolvedVideo = resolveMediaUrl(videoSrc);

  return (
    <div
      className={`relative overflow-hidden bg-onyx-800 ${className}`}
      style={style}
      data-testid={testId}
    >
      {(() => {
        if (videoSrc) {
          return (
            <video
              src={resolvedVideo}
              autoPlay
              loop
              muted
              playsInline
              className="h-full w-full object-cover"
            />
          );
        }
        if (src) {
          return (
            <img
              src={resolvedImg}
              alt={alt}
              loading="lazy"
              className="h-full w-full object-cover"
              onError={(e) => {
                e.currentTarget.src =
                  "https://images.unsplash.com/photo-1485846234645-a62644f84728?auto=format&fit=crop&w=1600&q=80";
              }}
            />
          );
        }
        return (
          <div className="flex h-full w-full items-center justify-center bg-onyx-700 text-ivory/40 text-sm font-cinzel tracking-widest">
            NO MEDIA
          </div>
        );
      })()}

      {/* Subtle gradient for legibility */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />

      {/* Gold D watermark - the official Dhadda Motion Pictures mark */}
      <div
        className={`pointer-events-none absolute ${posClass}`}
        style={{ opacity }}
      >
        <div className="rounded-sm bg-black/40 p-1.5 backdrop-blur-sm border border-gold/25">
          <Logo size={watermarkSize} />
        </div>
      </div>

      {children}
    </div>
  );
};

export default WatermarkedMedia;
